import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { PreferencesProvider } from "./contexts/PreferencesContext";
import { TutorialProvider } from "./contexts/TutorialContext";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";
import Tutorial from "./components/Tutorial";
import { PWAInstallBanner, OfflineIndicator } from "./components/PWAComponents";
import { OfflineSyncIndicator } from "./components/OfflineComponents";
import { registerServiceWorker } from "./hooks/usePWA";
import { cacheManager } from "./services/cacheManager";
import ErrorBoundary from "./components/ErrorBoundary";

// Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import FurnitureListPage from "./pages/FurnitureListPage";
import FurnitureDetailPage from "./pages/FurnitureDetailPage";
import FurnitureFormPage from "./pages/FurnitureFormPage";
import FurnitureTypesPage from "./pages/FurnitureTypesPage";
import ScanPage from "./pages/ScanPage";
import MovementsPage from "./pages/MovementsPage";
import InventoryPage from "./pages/InventoryPage";
import InventoryReportPage from "./pages/InventoryReportPage";
import LocationsPage from "./pages/LocationsPage";
import ReportsPage from "./pages/ReportsPage";
import UsersPage from "./pages/UsersPage";
import CatalogPage from "./pages/CatalogPage";
import ImportPage from "./pages/ImportPage";
import DocumentationPage from "./pages/DocumentationPage";
import SettingsPage from "./pages/SettingsPage";

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public Route Component (redirects if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <RegisterPage />
        </PublicRoute>
      } />

      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      
      <Route path="/furniture" element={
        <ProtectedRoute>
          <FurnitureListPage />
        </ProtectedRoute>
      } />
      <Route path="/furniture/new" element={
        <ProtectedRoute>
          <FurnitureFormPage />
        </ProtectedRoute>
      } />
      <Route path="/furniture/:id" element={
        <ProtectedRoute>
          <FurnitureDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/furniture/:id/edit" element={
        <ProtectedRoute>
          <FurnitureFormPage />
        </ProtectedRoute>
      } />

      <Route path="/scan" element={
        <ProtectedRoute>
          <ScanPage />
        </ProtectedRoute>
      } />

      <Route path="/movements" element={
        <ProtectedRoute>
          <MovementsPage />
        </ProtectedRoute>
      } />
      <Route path="/movements/new" element={
        <ProtectedRoute>
          <MovementsPage />
        </ProtectedRoute>
      } />

      <Route path="/inventory" element={
        <ProtectedRoute>
          <InventoryPage />
        </ProtectedRoute>
      } />
      
      <Route path="/inventory/:id/report" element={
        <ProtectedRoute>
          <InventoryReportPage />
        </ProtectedRoute>
      } />

      <Route path="/locations" element={
        <ProtectedRoute>
          <LocationsPage />
        </ProtectedRoute>
      } />

      <Route path="/furniture-types" element={
        <ProtectedRoute>
          <FurnitureTypesPage />
        </ProtectedRoute>
      } />

      <Route path="/catalog" element={
        <ProtectedRoute>
          <CatalogPage />
        </ProtectedRoute>
      } />

      <Route path="/reports" element={
        <ProtectedRoute>
          <ReportsPage />
        </ProtectedRoute>
      } />

      <Route path="/import" element={
        <ProtectedRoute>
          <ImportPage />
        </ProtectedRoute>
      } />

      <Route path="/users" element={
        <ProtectedRoute adminOnly>
          <UsersPage />
        </ProtectedRoute>
      } />

      <Route path="/documentation" element={
        <ProtectedRoute>
          <DocumentationPage />
        </ProtectedRoute>
      } />

      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />

      {/* Redirect root to dashboard or login */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      
      {/* 404 - Redirect to dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  // Register service worker on app mount
  useEffect(() => {
    registerServiceWorker();
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <PreferencesProvider>
          <TutorialProvider>
            <PWAInstallBanner />
            <Tutorial />
            <ErrorBoundary>
              <AppRoutes />
            </ErrorBoundary>
            <OfflineIndicator />
            <OfflineSyncIndicator />
            <Toaster position="top-right" />
          </TutorialProvider>
        </PreferencesProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
